/* Handing a file to the browser, and saying so.
 *
 * Defect 9 in BRIEF.md: "Clicking Export produces no visible response — no
 * menu, no toast, no confirmation. Either it fails or it downloads silently."
 * Both halves were true of the one export path this application had.
 *
 *   1. `downloadExport` built its anchor, clicked it, and then revoked the
 *      object URL on the same synchronous turn. A revoked URL is a dead URL:
 *      the click has been dispatched but the fetch the browser makes to satisfy
 *      the download has not started, and browsers differ on whether it wins the
 *      race. That is the "either it fails" half, and it fails silently because
 *      a cancelled download reports nothing to the page.
 *
 *   2. Nothing came back from it. It returned void, so no caller could have
 *      confirmed anything even if it had wanted to.
 *
 * So the delivery is one function, it revokes on a timer rather than on the
 * spot, and it returns what it did — the name and the size — for the caller to
 * put on screen. A browser never tells a page that a download completed, so the
 * confirmation is honest about what it knows: the file was built, it is this
 * big, and it went to the browser. It does not claim the file is on disk.
 */

/** What a delivery attempt did, for the line the user reads afterwards. */
export type Delivery =
  | { ok: true; fileName: string; bytes: number }
  | { ok: false; fileName: string; message: string };

/**
 * Long enough for any browser to have started reading the blob, short enough
 * that a reviewer exporting repeatedly does not accumulate them. Revoking is
 * what releases the memory the blob holds.
 */
const REVOKE_AFTER_MS = 60_000;

export function deliverBlob(blob: Blob, fileName: string): Delivery {
  try {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    // Kept out of the layout and out of the accessibility tree: it exists for
    // one click and is gone before anything can reach it.
    anchor.style.display = 'none';
    anchor.setAttribute('aria-hidden', 'true');
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    return { ok: true, fileName, bytes: blob.size };
  } catch (error) {
    return { ok: false, fileName, message: messageOf(error) };
  }
}

export function messageOf(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'The browser refused the file.';
}

/** "24 KB". Whole units: the point is that the file is not empty. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A file name a reviewer can find again: what it is, which review, and when.
 * Anything that is not a letter, a digit or a dash comes out as a dash, because
 * a document's own name may contain characters a file system will not take.
 */
export function exportFileName(
  stem: string,
  docName: string,
  extension: string,
  now: Date = new Date(),
): string {
  const slug =
    docName
      .replace(/\.[a-z0-9]{1,5}$/i, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'review';
  return `${stem}-${slug}-${now.toISOString().slice(0, 10)}.${extension}`;
}
